/**
 * Single source of truth for the public site origin (client + server).
 *
 * Uses `NEXT_PUBLIC_APP_URL` (preferred) or `NEXT_PUBLIC_SITE_URL`, then `VERCEL_URL`, then localhost
 * (see {@link getSiteUrl}). Server boot may set `NEXT_PUBLIC_SITE_URL` from `VERCEL_URL` on Vercel
 * (`applyServerEnvDefaults` in `lib/server/env-check.ts`).
 */

import { getEventPublicPath } from "@/lib/events/urls"

const PRODUCTION_SITE_ORIGIN = "https://events.maatii.co" as const

/** Canonical public app URL from env (trimmed). `NEXT_PUBLIC_APP_URL` wins over `NEXT_PUBLIC_SITE_URL`. */
export function trimPrimaryPublicAppUrlEnv(): string {
  const app = process.env.NEXT_PUBLIC_APP_URL?.trim() ?? ""
  if (app) return app
  return process.env.NEXT_PUBLIC_SITE_URL?.trim() ?? ""
}

function normalizeSiteOrigin(raw: string): string {
  const t = raw.trim().replace(/\/$/, "")
  if (!t) return "http://localhost:3000"
  if (t.startsWith("http://") || t.startsWith("https://")) return t
  return `https://${t}`
}

function originFromVercelUrl(): string {
  const vercel = process.env.VERCEL_URL?.trim()
  if (!vercel) return ""
  const host = vercel.replace(/^\/*/, "")
  return `https://${host}`.replace(/\/$/, "")
}

/**
 * Explicit public origin from env only (no localhost default).
 * Empty when unset — lets server routes fall back to `new URL(request.url).origin`.
 */
export function getConfiguredSiteOrigin(): string {
  const site = trimPrimaryPublicAppUrlEnv()
  if (site) return normalizeSiteOrigin(site)
  const fromVercel = originFromVercelUrl()
  if (fromVercel) return fromVercel
  if (process.env.NODE_ENV === "production") return PRODUCTION_SITE_ORIGIN
  return ""
}

/**
 * Public site origin for server / non-browser use.
 * - Development / tests: falls back to `http://localhost:3000` when unset.
 * - Production: returns empty string when unset (avoid wrong share links); set `NEXT_PUBLIC_APP_URL`,
 *   `NEXT_PUBLIC_SITE_URL`, or rely on `VERCEL_URL` at runtime.
 */
export function getSiteUrl(): string {
  const configured = getConfiguredSiteOrigin()
  if (configured) return configured
  if (process.env.NODE_ENV === "production") {
    return ""
  }
  return "http://localhost:3000"
}

/**
 * Browser: current tab origin. Server / first paint: {@link getSiteUrl} (env).
 * Use for share links and other client-built absolute URLs. Prefer **path-only** sign-in
 * `redirect_url` query values so SSR env (`VERCEL_URL`) never embeds a different host than
 * the browser (Clerk production domain allowlists).
 */
export function getPublicOriginForClientShell(): string {
  if (typeof window !== "undefined") {
    const o = window.location?.origin?.trim() ?? ""
    if (/^https?:\/\//i.test(o)) {
      return o.replace(/\/$/, "")
    }
  }
  return getSiteUrl().replace(/\/$/, "")
}

/** Absolute URL for the public event page (`app/event/[id]`). Path-only if no origin configured. */
export function getEventUrl(eventId: string): string {
  const base = getSiteUrl().replace(/\/$/, "")
  const path = getEventPublicPath({ id: eventId })
  if (!base) return path
  return `${base}${path}`
}
